import React from 'react';
import type {
    ReactNode,
    ComponentType,
    MouseEventHandler,
    HTMLAttributes,
    InputHTMLAttributes,
    SelectHTMLAttributes,
    ButtonHTMLAttributes,
    FocusEvent,
    ErrorInfo,
} from 'react';
import { Loader2, AlertCircle, Calendar } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import { formatMoney, sanitizeMoneyKeystroke } from '../../utils/numericInput';

/** A Lucide-style icon passed as a *component* (not an element) render-prop. */
type IconComponent = ComponentType<LucideProps>;

// --- CSS Variables & Theme Logic ---

/**
 * ThemeStyles Component
 * Injects CSS variables for the application theme based on the darkMode prop.
 * Handles the "Stone" color palette and transition animations.
 *
 * @param {Object} props
 * @param {boolean} props.darkMode - Whether dark mode is enabled
 */
export const ThemeStyles = ({ darkMode }: { darkMode: boolean }) => (
    <style>{`
    :root {
      /* Hestia Palette */
      /* Surface Hierarchy: layered depth via background shifts */
      --bg-main: ${darkMode ? '#0c0f0f' : '#f9f9f8'};
      --bg-card: ${darkMode ? '#161c1b' : '#ffffff'};
      --bg-hover: ${darkMode ? '#1e2524' : '#f3f4f3'};
      --bg-elevated: ${darkMode ? '#252c2b' : '#edeeed'};
      --bg-elevated-high: ${darkMode ? '#2e3534' : '#e7e8e7'};
      --bg-dim: ${darkMode ? '#0a0d0d' : '#d9dad9'};

      --text-main: ${darkMode ? '#e1e3e2' : '#191c1c'};
      --text-secondary: ${darkMode ? '#c1c8c5' : '#414846'};
      --text-muted: ${darkMode ? '#8a918e' : '#717976'};

      /* Primary: Deep Forest Green */
      --primary: ${darkMode ? '#abcec6' : '#032521'};
      --primary-text: ${darkMode ? '#00201c' : '#ffffff'};
      --primary-bg: ${darkMode ? 'rgba(27, 59, 54, 0.5)' : '#c7eae2'};
      --primary-bg-dim: ${darkMode ? 'rgba(3, 37, 33, 0.6)' : '#abcec6'};
      --primary-container: ${darkMode ? '#2d4c47' : '#1b3b36'};
      --primary-container-text: ${darkMode ? '#c7eae2' : '#84a59e'};

      /* Secondary: Stable Blue */
      --secondary: ${darkMode ? '#b1c9e1' : '#4a6175'};
      --secondary-container: ${darkMode ? '#32495c' : '#cae2fa'};
      --secondary-container-text: ${darkMode ? '#cde5fd' : '#4e6579'};

      /* Tertiary: Warm Earthy Accents */
      --tertiary-fixed: ${darkMode ? '#603e34' : '#ffdbd0'};
      --tertiary-fixed-dim: ${darkMode ? '#4d2e24' : '#ecbcad'};
      --tertiary-container: ${darkMode ? '#ecbcad' : '#4d2e24'};

      /* Semantic */
      --error: ${darkMode ? '#ffb4ab' : '#ba1a1a'};
      --error-container: ${darkMode ? '#93000a' : '#ffdad6'};

      /* "Unknown" palette — amber. Used for scan items the backend
         ingest doesn't recognize (e.g. a future RoomPlan SDK ships a
         new top-level array). Distinct from --error so a selected
         Unknown item (which turns red like every other selection) is
         visually distinguishable from an unselected one. */
      --unknown: ${darkMode ? '#fbbf24' : '#b45309'};
      --unknown-container: ${darkMode ? '#78350f' : '#fde68a'};

      /* Borders & Shadows */
      --border: ${darkMode ? 'rgba(193, 200, 197, 0.1)' : 'rgba(193, 200, 197, 0.2)'};
      --border-visible: ${darkMode ? '#414846' : '#c1c8c5'};
      --shadow: ${darkMode ? '0 12px 32px rgba(0, 0, 0, 0.3)' : '0 12px 32px rgba(3, 37, 33, 0.06)'};
      --shadow-sm: ${darkMode ? '0 4px 12px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(3, 37, 33, 0.04)'};
    }

    .theme-transition {
      transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
    }

    .scan-line {
      animation: scan 2s linear infinite;
    }

    .progress-bar {
      transition: width 1s ease-in-out;
    }

    body {
        background-color: var(--bg-main);
        color: var(--text-main);
        font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    }

    h1, h2, h3, h4 {
        font-family: 'Manrope', system-ui, sans-serif;
    }

    .glass-header {
        background: ${darkMode ? 'rgba(12, 15, 15, 0.8)' : 'rgba(249, 249, 248, 0.8)'};
        backdrop-filter: blur(20px);
    }

    @keyframes scan {
      0% { top: 0%; opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { top: 100%; opacity: 0; }
    }

    .animate-fade-in {
        animation: fadeIn 0.5s ease-out both;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
  `}</style>
);

// --- Components ---

/**
 * Card Component
 * A basic container component with consistent styling, rounded corners, and shadow.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display inside the card
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    className?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
}
export const Card = ({ children, className = "", onClick, ...rest }: CardProps) => (
    <div
        onClick={onClick}
        className={`bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-sm)] theme-transition ${className}`}
        {...rest}
    >
        {children}
    </div>
);

/**
 * Badge Component
 * Displays a status label with appropriate color coding.
 *
 * @param {Object} props
 * @param {string} props.status - The status text to display (e.g., 'Good', 'Pending', 'Overdue')
 */
/** The controlled vocabulary that drives Badge styling. Both `status` and
 *  `variant` are typed to this union; anything outside it falls through to the
 *  neutral default style (and is rejected at compile time for typed callers). */
export type BadgeVariant =
    | 'Good'
    | 'Completed'
    | 'Routine'
    | 'High Priority'
    | 'Overdue'
    | 'Service Due'
    | 'Pending'
    | 'Scheduled'
    | 'In Progress'
    | 'Active Maintenance'
    | 'Warning';

export interface BadgeProps {
    /** Free-form display text. Falls back to `variant` when omitted. This is
     *  the label *shown*; it does not affect styling. */
    label?: ReactNode;
    /** Style key (controlled vocabulary). Also used as the displayed text when
     *  neither `label` nor `status` is provided. */
    variant?: BadgeVariant;
    /** @deprecated Legacy prop that conflated display text with the style key.
     *  Prefer `label` (text) + `variant` (style). Still honored for styling and
     *  as a display fallback so un-migrated .jsx callers keep working. */
    status?: BadgeVariant;
}
export const Badge = ({ label, status, variant }: BadgeProps) => {
    // Styling key: explicit variant wins, then the legacy status key.
    const v = variant || status;
    let styles: string;

    switch (v) {
        case 'Good':
        case 'Completed':
        case 'Routine':
            styles = 'bg-[var(--primary-bg)] text-[var(--primary)]';
            break;
        case 'High Priority':
        case 'Overdue':
        case 'Service Due':
            styles = 'bg-[var(--tertiary-fixed)] text-[var(--primary)]';
            break;
        case 'Pending':
        case 'Scheduled':
        case 'In Progress':
            styles = 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]';
            break;
        case 'Active Maintenance':
            styles = 'bg-[var(--secondary-container)] text-[var(--secondary-container-text)]';
            break;
        case 'Warning':
            styles = 'bg-[var(--error-container)] text-[var(--error)]';
            break;
        default:
            styles = 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]';
    }

    // Display text: explicit label wins, then legacy status, then the variant
    // key itself (preserves the prior `status || variant` fallback behavior).
    const text = label ?? status ?? variant;

    return (
        // whitespace-nowrap + shrink-0: labels are short, and a wrapped pill
        // ("3 / STATEMENTS" on two lines) reads as a layout bug rather than a badge.
        <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider whitespace-nowrap shrink-0 ${styles}`}>
            {text}
        </span>
    );
};

// --- Form & Interactive Components ---

/** A single tab descriptor for V2Tabs / SegmentedTabs. */
export interface TabItem {
    key: string | number;
    label: ReactNode;
    icon?: IconComponent;
    /**
     * Forwarded verbatim to the rendered button's data-testid. E2E anchor —
     * e2e/pages/maintenance/CreateTaskPage.ts relies on the exact
     * `frequency-mode-*` values, so never rename them at a call site.
     */
    testId?: string;
}

/**
 * V2Tabs Component
 * A tab navigation component.
 *
 * @param {Object} props
 * @param {string|number} props.activeTab - The key of the currently active tab
 * @param {Function} props.onSelect - Callback when a tab is selected
 * @param {Array} props.tabs - Array of tab objects { key, label, icon: Component }
 * @param {string} [props.className] - Additional classes
 */
export interface V2TabsProps {
    activeTab: string | number;
    onSelect: (key: string | number) => void;
    tabs: TabItem[];
    className?: string;
}
export const V2Tabs = ({ activeTab, onSelect, tabs, className = "" }: V2TabsProps) => (
    <div className={`flex space-x-1 border-b border-[var(--border)] overflow-x-auto no-scrollbar ${className}`}>
        {tabs.map((tab) => (
            <button
                key={tab.key}
                type="button"
                onClick={() => onSelect(tab.key)}
                className={`
                    flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                    ${activeTab === tab.key
                        ? 'border-[var(--primary)] text-[var(--primary)]'
                        : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-main)] hover:border-[var(--text-muted)]'}
                `}
            >
                {tab.icon && <tab.icon size={16} />}
                <span>{tab.label}</span>
            </button>
        ))}
    </div>
);

/**
 * Button Component
 * Shared button primitive for the V2 UI. Encapsulates variants, sizes, and
 * corner radius so interactive surfaces stay consistent with Card's rounding.
 *
 * Props
 * @param {'primary'|'primaryGradient'|'secondary'|'ghost'|'destructive'} [variant='primary']
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {'2xl'|'xl'|'full'} [radius='2xl']  Default matches Card (rounded-2xl).
 * @param {React.ComponentType} [leadingIcon]  Lucide icon component (not element).
 * @param {React.ComponentType} [trailingIcon]
 * @param {boolean} [loading]   Shows spinner in the leading-icon slot and disables click.
 * @param {boolean} [disabled]
 * @param {boolean} [fullWidth]
 * @param {string}  [className]  Appended last so callers can override any class.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'primaryGradient' | 'secondary' | 'ghost' | 'destructive';
    size?: 'sm' | 'md' | 'lg';
    radius?: '2xl' | 'xl' | 'full';
    leadingIcon?: IconComponent;
    trailingIcon?: IconComponent;
    loading?: boolean;
    fullWidth?: boolean;
}
export const Button = ({
    variant = 'primary',
    size = 'md',
    radius = '2xl',
    leadingIcon: LeadingIcon,
    trailingIcon: TrailingIcon,
    loading = false,
    disabled = false,
    fullWidth = false,
    className = '',
    children,
    ...rest
}: ButtonProps) => {
    let variantStyles: string;
    switch (variant) {
        case 'primaryGradient':
            variantStyles = 'bg-gradient-to-r from-[var(--primary)] to-[var(--primary-container)] text-white shadow-lg hover:opacity-90';
            break;
        case 'secondary':
            variantStyles = 'bg-[var(--bg-card)] text-[var(--primary)] shadow-[var(--shadow-sm)] hover:shadow-md';
            break;
        case 'ghost':
            variantStyles = 'bg-transparent text-[var(--text-main)] hover:bg-[var(--bg-hover)]';
            break;
        case 'destructive':
            variantStyles = 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400';
            break;
        case 'primary':
        default:
            variantStyles = 'bg-[var(--primary)] text-white hover:opacity-90';
    }

    const sizeStyles = {
        sm: 'px-3 py-1.5 text-sm gap-1.5',
        md: 'px-6 py-3 text-base gap-2',
        lg: 'px-8 py-4 text-lg gap-2.5',
    }[size];

    const radiusStyles = {
        '2xl': 'rounded-2xl',
        xl: 'rounded-xl',
        full: 'rounded-full',
    }[radius];

    const iconSize = { sm: 14, md: 18, lg: 20 }[size];

    return (
        <button
            disabled={disabled || loading}
            className={`appearance-none inline-flex items-center justify-center font-semibold whitespace-nowrap transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${variantStyles} ${sizeStyles} ${radiusStyles} ${fullWidth ? 'w-full' : ''} ${className}`}
            {...rest}
        >
            {loading
                ? <Loader2 size={iconSize} className="animate-spin" />
                : LeadingIcon && <LeadingIcon size={iconSize} />}
            {children && <span>{children}</span>}
            {!loading && TrailingIcon && <TrailingIcon size={iconSize} />}
        </button>
    );
};

/**
 * FormSection Component
 * A container for grouping form fields with a title.
 */
export interface FormSectionProps {
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
}
export const FormSection = ({ title, children, className = "" }: FormSectionProps) => (
    <div className={`mb-8 ${className}`}>
        {title && <h3 className="text-lg font-bold text-[var(--text-main)] mb-4">{title}</h3>}
        {children}
    </div>
);

/**
 * InputGroup Component
 * Wraps an input with a label and error message.
 */
export interface InputGroupProps {
    label?: string;
    /** Usually a single form-control element, which InputGroup clones to
     *  inject `id`. Multiple children (e.g. an input plus a hint <p>) are
     *  rendered as-is — cloneElement on an array produces an element with
     *  `type: undefined` and crashes the whole page. */
    children: ReactNode;
    error?: ReactNode;
    required?: boolean;
    id?: string;
}
export const InputGroup = ({ label, children, error, required, id }: InputGroupProps) => {
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
        <div className="mb-4">
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1"
                >
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            {React.isValidElement(children)
                ? React.cloneElement(children, { id: inputId } as Partial<unknown>)
                : children}
            {error && (
                <p className="mt-1 text-xs text-red-500">
                    {/* Backend field errors arrive as arrays of strings
                        (WTForms .errors); joining keeps consumers from
                        rendering run-together messages. Arrays containing
                        React elements are rendered as-is — join() would
                        stringify them to "[object Object]". */}
                    {Array.isArray(error) && error.every((e) => typeof e === 'string')
                        ? error.join(' ')
                        : error}
                </p>
            )}
        </div>
    );
};

/**
 * Input Component
 * A styled input field.
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: ReactNode;
}
export const Input = ({ className = "", error, ...props }: InputProps) => (
    <input
        className={`
            w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border transition-all text-[var(--text-main)]
            focus:ring-2 focus:ring-[var(--primary)] focus:outline-none placeholder-gray-400
            ${error ? 'border-red-500 focus:border-red-500' : 'border-[var(--border)]'}
            ${className}
        `}
        {...props}
    />
);

/** Synthetic change payload emitted by MoneyInput — shaped like a DOM change
 *  event's `target` so it drops into the same handleInputChange handlers the
 *  plain Input uses. */
export interface MoneyInputChangeEvent {
    target: { name?: string; value: string };
}

/**
 * MoneyInput Component
 * Currency text field with a leading "$" that always normalizes to two decimal
 * places on blur (e.g. "242854" → "242854.00", "1738.8" → "1738.80"). Uses
 * type="text" + inputMode="decimal" so the formatted value — including trailing
 * zeros — can actually be displayed (a type="number" input strips them) and so
 * there are no number-spinner steppers. Emits a synthetic
 * { target: { name, value } } change event, so it drops into the same
 * handleInputChange handlers the plain Input uses.
 *
 * @param {string|number} value             Controlled value from form state.
 * @param {Function} onChange               Receives { target: { name, value } }.
 * @param {string} name
 * @param {boolean} [allowNegative=false]   Permit a leading minus (e.g. escrow shortage).
 */
export interface MoneyInputProps
    extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'name' | 'type'> {
    value?: string | number;
    onChange?: (event: MoneyInputChangeEvent) => void;
    name?: string;
    allowNegative?: boolean;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}
export const MoneyInput = ({
    value,
    onChange,
    name,
    allowNegative = false,
    className = "",
    onBlur: externalOnBlur,
    onFocus: externalOnFocus,
    ...props
}: MoneyInputProps) => {
    const [display, setDisplay] = React.useState<string>(() => formatMoney(value));
    const [focused, setFocused] = React.useState(false);

    // Mirror the external value (formatted) whenever it changes and we're not
    // mid-edit. Skipping while focused avoids snapping "100." → "100.00" on
    // every keystroke, which would block typing the decimal portion.
    React.useEffect(() => {
        if (!focused) setDisplay(formatMoney(value));
    }, [value, focused]);

    const emit = (v: string) => onChange?.({ target: { name, value: v } });

    return (
        <div className="relative">
            <span className="absolute left-3 top-2.5 text-[var(--text-muted)] text-sm">$</span>
            <Input
                {...props}
                name={name}
                type="text"
                inputMode="decimal"
                className={`pl-7 ${className}`}
                value={display}
                onFocus={(e) => {
                    setFocused(true);
                    externalOnFocus?.(e);
                }}
                onChange={(e) => {
                    const v = sanitizeMoneyKeystroke(e.target.value, allowNegative);
                    setDisplay(v);
                    emit(v);
                }}
                onBlur={(e) => {
                    setFocused(false);
                    const v = formatMoney(display);
                    setDisplay(v);
                    emit(v);
                    externalOnBlur?.(e);
                }}
            />
        </div>
    );
};

/**
 * Select Component
 * A styled select dropdown.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    children?: ReactNode;
    /** Renders a leading empty-value option. Use this instead of hand-typing
     *  a placeholder <option> per page — hand-typed copies drift
     *  ("-- Select --" vs "— Select —"). */
    placeholder?: string;
}
export const Select = ({ className = "", children, placeholder, ...props }: SelectProps) => (
    <select
        className={`
            w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)]
            focus:ring-2 focus:ring-[var(--primary)] focus:outline-none
            ${className}
        `}
        {...props}
    >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {children}
    </select>
);

/**
 * LoadingState Component
 * Centered spinner for page/section loads. `fullPage` centers within the
 * viewport height; the default height matches the in-content spinner idiom.
 */
export interface LoadingStateProps {
    fullPage?: boolean;
    size?: number;
    className?: string;
}
export const LoadingState = ({ fullPage = false, size = 48, className = '' }: LoadingStateProps) => (
    <div
        role="status"
        aria-label="Loading"
        className={`flex justify-center items-center ${fullPage ? 'h-screen' : 'h-64'} ${className}`}
    >
        <Loader2 className="animate-spin text-[var(--primary)]" size={size} />
    </div>
);

/**
 * ErrorState Component
 * Centered error block with a required recovery path: pass `onRetry` for
 * transient failures and/or a secondary `action` (e.g. "Back to Properties").
 * Consolidates the hand-rolled error UIs that drifted across pages (some had
 * Retry, some didn't — see docs/houses/ux-review-2026-07.md).
 */
export interface ErrorStateProps {
    title?: string;
    message?: ReactNode;
    onRetry?: () => void;
    retryLabel?: string;
    action?: { label: string; onClick: () => void };
    className?: string;
}
export const ErrorState = ({
    title = 'Something went wrong',
    message,
    onRetry,
    retryLabel = 'Retry',
    action,
    className = '',
}: ErrorStateProps) => (
    <div className={`flex flex-col items-center justify-center p-10 text-center ${className}`}>
        <div className="p-3 bg-[var(--error-container)] text-[var(--error)] rounded-full mb-4">
            <AlertCircle size={32} />
        </div>
        <h3 className="text-xl font-bold text-[var(--text-main)] mb-2">{title}</h3>
        {message && <p className="text-[var(--text-secondary)] mb-6">{message}</p>}
        {(onRetry || action) && (
            <div className="flex gap-3">
                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                        {retryLabel}
                    </button>
                )}
                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        className="px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                    >
                        {action.label}
                    </button>
                )}
            </div>
        )}
    </div>
);

/**
 * EmptyState Component
 * Centered "nothing here yet" block with an optional CTA. Show the CTA only
 * in the truly-empty case, not when a filter/search returned no results
 * (canonical contextual usage: Inventory.tsx).
 */
export interface EmptyStateProps {
    icon?: IconComponent;
    title: string;
    message?: ReactNode;
    actionLabel?: string;
    onAction?: () => void;
    className?: string;
}
export const EmptyState = ({ icon: Icon, title, message, actionLabel, onAction, className = '' }: EmptyStateProps) => (
    <div className={`flex flex-col items-center justify-center text-center py-12 ${className}`}>
        {Icon && (
            <div className="p-4 bg-[var(--bg-hover)] rounded-full mb-4 text-[var(--text-muted)]">
                <Icon size={48} className="opacity-50" />
            </div>
        )}
        <h4 className="text-xl font-bold text-[var(--text-main)] mb-2">{title}</h4>
        {message && <p className="text-[var(--text-secondary)] mb-6 max-w-md">{message}</p>}
        {actionLabel && onAction && (
            <button
                type="button"
                onClick={onAction}
                className="px-6 py-3 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium"
            >
                {actionLabel}
            </button>
        )}
    </div>
);

/**
 * PageHeader Component
 * Title + optional subtitle on the left, actions on the right. flex-wrap is
 * load-bearing: without it the actions overlap the title on narrow viewports
 * (the exact mobile bug HouseForm shipped with).
 */
export interface PageHeaderProps {
    title: ReactNode;
    subtitle?: ReactNode;
    actions?: ReactNode;
    className?: string;
}
export const PageHeader = ({ title, subtitle, actions, className = '' }: PageHeaderProps) => (
    <div className={`flex flex-wrap justify-between items-center gap-3 mb-6 ${className}`}>
        <div>
            <h1 className="text-3xl font-bold text-[var(--text-main)] mb-1 theme-transition">{title}</h1>
            {subtitle && <p className="text-[var(--text-secondary)] theme-transition">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
);

/**
 * DateField Component
 * Input type=date with a leading Calendar icon for a clearer affordance.
 * Native HTML5 date inputs put a tiny picker glyph on the right (poorly
 * visible in dark mode) and use a browser-styled `mm/dd/yyyy` placeholder
 * that can read like a hint to type a string. The leading icon makes
 * "this is a date field" unambiguous before the user clicks. Mirrors the
 * dollar-sign treatment on currency inputs. (Promoted from HouseForm.)
 */
export const DateField = ({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) => (
    <div className="relative">
        <Calendar
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
            aria-hidden="true"
        />
        <Input {...rest} type="date" className={`pl-9 ${className}`} />
    </div>
);

// Toggle Switch Component
/**
 * Toggle Component
 * A switch toggle for boolean states.
 *
 * @param {Object} props
 * @param {boolean} props.enabled - Current state of the toggle
 * @param {Function} props.onChange - Callback function when toggled, receives new state
 */
export interface ToggleProps {
    enabled: boolean;
    onChange: (next: boolean) => void;
    disabled?: boolean;
    /** Accessible name — the toggle renders no text, so without this a
     *  screen reader announces an unnamed switch. Pass what it controls,
     *  e.g. "Dark mode". */
    ariaLabel?: string;
}
export const Toggle = ({ enabled, onChange, disabled = false, ariaLabel = 'Toggle' }: ToggleProps) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled ? 'true' : 'false'}
        aria-label={ariaLabel}
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative inline-flex h-8 w-14 items-center !rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${enabled ? 'bg-[var(--primary)]' : 'bg-[var(--bg-elevated-high)]'
            }`}
    >
        <span
            className={`inline-block h-6 w-6 transform !rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${enabled ? 'translate-x-7' : 'translate-x-1'
                }`}
        />
    </button>
);

/**
 * ConfirmationModal Component
 * A modal dialog for confirming actions, particularly destructive ones.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - request to close the modal
 * @param {Function} props.onConfirm - Action to execute on confirmation
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal body text
 * @param {string} [props.confirmText='Confirm'] - Text for confirm button
 * @param {string} [props.cancelText='Cancel'] - Text for cancel button
 * @param {boolean} [props.isDanger=false] - If true, styles the confirm button as destructive (red)
 */
export interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: ReactNode;
    message?: ReactNode;
    confirmText?: string;
    cancelText?: string;
    isDanger?: boolean;
    isLoading?: boolean;
    icon?: IconComponent | null;
}
export const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', cancelText = 'Cancel', isDanger = false, isLoading = false, icon: Icon = null }: ConfirmationModalProps) => {
    // Add escape key support
    React.useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
        }
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirmation-modal-title"
            aria-describedby="confirmation-modal-description"
        >
            <div
                className={`bg-[var(--bg-card)] w-full max-w-sm rounded-2xl shadow-xl p-6 theme-transition ${Icon ? 'text-center' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {Icon && (
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full dark:bg-red-900/30">
                        <Icon className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                )}
                <h3 id="confirmation-modal-title" className="text-xl font-bold text-[var(--text-main)] mb-2 theme-transition">{title}</h3>
                <p id="confirmation-modal-description" className="text-[var(--text-secondary)] mb-6 theme-transition text-sm">{message}</p>
                <div className="flex space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition font-medium disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={() => { onConfirm(); if (!isLoading) onClose(); }}
                        disabled={isLoading}
                        className={`flex-1 py-2 flex items-center justify-center rounded-lg font-medium text-white transition disabled:opacity-50 ${isDanger ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--primary)] hover:opacity-90'}`}
                    >
                        {isLoading ? 'Confirming...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Simple event bus for Toasts
type ToastType = 'success' | 'error' | 'info';
interface ToastMessage {
    type: ToastType;
    message: string;
    id: number;
}
type ToastListener = (toast: ToastMessage) => void;

const toastListeners: ToastListener[] = [];
let toastIdCounter = 0;
const emitToast = (type: ToastType, message: string) => {
    toastIdCounter += 1;
    toastListeners.forEach(listener => listener({ type, message, id: toastIdCounter }));
};

/**
 * Toast Helper
 * Public API for triggering toast notifications.
 * @example
 * toast.success('Saved successfully');
 * toast.error('An error occurred');
 */
export const toast = {
    success: (msg: string) => emitToast('success', msg),
    error: (msg: string) => emitToast('error', msg),
    info: (msg: string) => emitToast('info', msg)
};

/**
 * ToastContainer Component
 * Renders the active toast notifications. Should be placed once in the app layout.
 * Listens to events emitted by the `toast` helper.
 */
export const ToastContainer = () => {
    const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

    React.useEffect(() => {
        const activeTimeouts: ReturnType<typeof setTimeout>[] = [];
        const listener: ToastListener = (newToast) => {
            setToasts(prev => [...prev, newToast]);
            // Auto remove after 3s
            const timeoutId = setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== newToast.id));
            }, 3000);
            activeTimeouts.push(timeoutId);
        };
        toastListeners.push(listener);
        return () => {
            const index = toastListeners.indexOf(listener);
            if (index > -1) toastListeners.splice(index, 1);
            // Clear all active timeouts on unmount
            activeTimeouts.forEach(id => clearTimeout(id));
        };
    }, []);

    return (
        <div className="fixed bottom-4 right-4 z-[70] flex flex-col gap-2 pointer-events-none">
            {toasts.map(t => (
                <div
                    key={t.id}
                    className={`
                        min-w-[300px] p-4 rounded-lg shadow-lg border animate-fade-in pointer-events-auto
                        ${t.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
                            t.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
                                'bg-blue-50 text-blue-800 border-blue-200'}
                    `}
                >
                    <p className="text-sm font-medium">{t.message}</p>
                </div>
            ))}
        </div>
    );
};

// --- Hestia Design System Components ---

/**
 * StatusTile Component
 * Colored stat card used for dashboard/maintenance status overviews.
 */
export interface StatusTileProps {
    label: ReactNode;
    value: ReactNode;
    icon?: IconComponent;
    variant?: 'overdue' | 'due-soon' | 'completed' | 'default';
}
export const StatusTile = ({ label, value, icon: Icon, variant = 'default' }: StatusTileProps) => {
    const variants: Record<NonNullable<StatusTileProps['variant']>, string> = {
        overdue: 'bg-[var(--tertiary-fixed)]',
        'due-soon': 'bg-[var(--secondary-container)]',
        completed: 'bg-[var(--primary-bg)]',
        default: 'bg-[var(--bg-elevated)]',
    };

    return (
        <div className={`${variants[variant]} p-6 rounded-2xl flex items-center justify-between theme-transition`}>
            <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-secondary)] opacity-80">{label}</p>
                <p className="text-4xl font-black mt-1 text-[var(--text-main)]">{value}</p>
            </div>
            {Icon && <Icon size={48} className="text-[var(--text-muted)] opacity-40" />}
        </div>
    );
};

/**
 * Builds the DOM id for a SegmentedTabs item. Exported so a consumer can point
 * its tabpanel's aria-labelledby at the active tab without duplicating the
 * format string.
 */
export const segmentedTabId = (idPrefix: string, key: string | number): string =>
    `${idPrefix}-tab-${key}`;

interface SegmentedTabsBaseProps {
    tabs: TabItem[];
    activeTab: string | number;
    onSelect: (key: string | number) => void;
    className?: string;
}

/**
 * The group's accessible name — exactly one, enforced at compile time. Both is
 * contradictory (aria-labelledby would win and silently shadow aria-label), and
 * neither leaves an unnamed tablist/radiogroup, which is an accessibility defect
 * with no runtime symptom.
 */
type SegmentedTabsNameProps =
    | { ariaLabel: string; ariaLabelledBy?: never }
    | { ariaLabel?: never; ariaLabelledBy: string };

/**
 * `semantics` selects the ARIA pattern:
 *   - 'tabs' (default) → tablist/tab/aria-selected, for a strip that swaps
 *     panels. `idPrefix` and `panelId` are REQUIRED at compile time: without
 *     them the tab↔panel relationship silently breaks (no `id` to point
 *     aria-labelledby at, and a dangling `aria-controls`).
 *   - 'radiogroup' → radiogroup/radio/aria-checked, for a strip that picks one
 *     of N form values. A radio group controls no panel, so the two id props
 *     are forbidden rather than merely unused.
 * Both get the same keyboard contract, which ARIA mandates for either pattern.
 */
type SegmentedTabsSemanticsProps =
    | { semantics?: 'tabs'; idPrefix: string; panelId: string }
    | { semantics: 'radiogroup'; idPrefix?: never; panelId?: never };

export type SegmentedTabsProps =
    SegmentedTabsBaseProps & SegmentedTabsNameProps & SegmentedTabsSemanticsProps;

/**
 * SegmentedTabs Component
 * Pill-style segmented control for 2–4 short options. Replaces the fixed-width
 * `inline-flex` pattern that clipped on every mobile viewport.
 *
 * Sizing contract — do not "simplify" these classes away:
 *   - container is `flex w-full`, NOT `inline-flex`: the strip must be able to
 *     shrink with its parent instead of taking intrinsic content width.
 *   - items are `flex-auto min-w-0` (flex: 1 1 auto), NOT `flex-1` (1 1 0%):
 *     flex-auto shrinks proportionally to content width, so a long label yields
 *     more pixels than a short one and they reach the ellipsis together;
 *     flex-1 would force equal columns and starve the longest label.
 *   - the label span is `truncate`, and that is mandatory. `min-w-0` plus
 *     `whitespace-nowrap` without `overflow: hidden` lets text paint over the
 *     neighbouring pill and past the container's rounded edge.
 *   - `min-h-11` gives a 44px touch target; py-2.5 + text-sm alone is only 40px.
 *
 * There is deliberately no `overflow-x-auto`: shrinkable flex items always sum
 * to the container width, so scrollWidth equals clientWidth and a scrollbar can
 * never appear. Shrink and scroll are mutually exclusive — for a strip that
 * needs to scroll (more items, or long labels) use V2Tabs instead.
 */
export const SegmentedTabs = ({
    tabs,
    activeTab,
    onSelect,
    semantics = 'tabs',
    ariaLabel,
    ariaLabelledBy,
    idPrefix,
    panelId,
    className = "",
}: SegmentedTabsProps) => {
    const isTabs = semantics === 'tabs';
    const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

    // Roving tabindex needs exactly one tabbable item. When activeTab matches no
    // key — a stale or transient value while tabs/activeTab update out of sync —
    // fall back to the first item, otherwise every item gets tabIndex={-1} and
    // the whole strip drops out of the tab order. This is deliberately separate
    // from `active` below: with no match, nothing is aria-selected/checked, which
    // is the honest state, but the control stays reachable.
    const activeIndex = tabs.findIndex((t) => t.key === activeTab);
    const focusIndex = activeIndex === -1 ? 0 : activeIndex;

    // Automatic activation: an arrow key both moves focus and selects, which is
    // correct for tabs and required for radio. Focus is moved imperatively here
    // rather than in an effect keyed on activeTab — consumers change activeTab
    // programmatically (e.g. Maintenance's "View series history" button), and an
    // effect would yank focus away from wherever the user actually is.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!tabs.length) return;

        // Keyed off focusIndex, not activeIndex, so arrows still work from the
        // fallback position instead of dead-ending on an unmatched activeTab.
        const current = focusIndex;
        const last = tabs.length - 1;
        let next: number | null = null;

        switch (e.key) {
            case 'ArrowRight':
                next = current === last ? 0 : current + 1;
                break;
            case 'ArrowLeft':
                next = current === 0 ? last : current - 1;
                break;
            // Up/Down belong to radiogroup only; a horizontal tablist must leave
            // them alone so they keep scrolling the page.
            case 'ArrowDown':
                if (isTabs) return;
                next = current === last ? 0 : current + 1;
                break;
            case 'ArrowUp':
                if (isTabs) return;
                next = current === 0 ? last : current - 1;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = last;
                break;
            default:
                return;
        }

        e.preventDefault();
        onSelect(tabs[next].key);
        itemRefs.current[next]?.focus();
    };

    return (
        <div
            role={isTabs ? 'tablist' : 'radiogroup'}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-orientation="horizontal"
            onKeyDown={handleKeyDown}
            className={`flex w-full p-1 rounded-xl bg-[var(--bg-hover)] border border-[var(--border)] ${className}`}
        >
            {tabs.map((tab, i) => {
                const active = activeTab === tab.key;
                return (
                    <button
                        key={tab.key}
                        ref={(el) => { itemRefs.current[i] = el; }}
                        type="button"
                        role={isTabs ? 'tab' : 'radio'}
                        id={isTabs && idPrefix ? segmentedTabId(idPrefix, tab.key) : undefined}
                        aria-selected={isTabs ? active : undefined}
                        aria-checked={isTabs ? undefined : active}
                        aria-controls={isTabs ? panelId : undefined}
                        tabIndex={i === focusIndex ? 0 : -1}
                        data-testid={tab.testId}
                        // Keeps a truncated label readable. `title` does not
                        // override the accessible name (element content wins),
                        // so name-based locators keep resolving.
                        title={typeof tab.label === 'string' ? tab.label : undefined}
                        onClick={() => onSelect(tab.key)}
                        className={`
                            flex-auto min-w-0 min-h-11 flex items-center justify-center gap-1.5
                            px-2 sm:px-4 md:px-6 py-2.5 rounded-lg text-sm transition-all
                            ${active
                                ? 'bg-[var(--bg-card)] text-[var(--primary)] font-bold shadow-[var(--shadow-sm)]'
                                : 'text-[var(--text-secondary)] font-medium hover:text-[var(--text-main)]'}
                        `}
                    >
                        {tab.icon && <tab.icon size={16} className="shrink-0" />}
                        <span className="truncate">{tab.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * SpecCard Component
 * Quick specification card with icon, label, and value (used in Room Details).
 */
export interface SpecCardProps {
    icon?: IconComponent;
    label: ReactNode;
    value: ReactNode;
    detail?: ReactNode;
    variant?: 'default' | 'dark';
    className?: string;
}
export const SpecCard = ({ icon: Icon, label, value, detail, variant = 'default', className = "" }: SpecCardProps) => {
    const isDark = variant === 'dark';

    return (
        <div className={`p-6 rounded-[1.5rem] flex flex-col gap-4 ${
            isDark
                ? 'bg-[var(--primary-container)] text-white'
                : 'bg-[var(--bg-hover)]'
        } ${className}`}>
            {Icon && (
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark
                        ? 'bg-white/10 text-[var(--primary-container-text)]'
                        : 'bg-[var(--primary-bg)] text-[var(--primary)]'
                }`}>
                    <Icon size={20} />
                </div>
            )}
            <div>
                <p className={`text-sm font-medium ${isDark ? 'text-[var(--primary-container-text)]' : 'text-[var(--text-secondary)]'}`}>{label}</p>
                <p className={`font-headline font-bold text-xl ${isDark ? 'text-white' : 'text-[var(--text-main)]'}`}>{value}</p>
                {detail && (
                    <p className={`text-[11px] mt-1 uppercase tracking-tight ${isDark ? 'opacity-60' : 'text-[var(--text-muted)]'}`}>{detail}</p>
                )}
            </div>
        </div>
    );
};

/**
 * ErrorBoundary Component
 * Catches JavaScript errors in the child tree, logs them, and renders a
 * themed fallback UI. Provides:
 *   - Dev-mode error details (componentStack inside <details>)
 *   - "Go to Dashboard" recovery via React Router's useNavigate
 *   - Force-remount on Try Again via errorCount key (clears broken hook state)
 *
 * Must be rendered inside <Router> so useNavigate resolves.
 */
interface ErrorBoundaryClassProps {
    children?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    navigate?: NavigateFunction;
}
interface ErrorBoundaryClassState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    errorCount: number;
}
class ErrorBoundaryClass extends React.Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
    constructor(props: ErrorBoundaryClassProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, errorCount: 0 };
    }

    static getDerivedStateFromError(): Partial<ErrorBoundaryClassState> {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        this.setState((prev) => ({
            error,
            errorInfo,
            errorCount: prev.errorCount + 1,
        }));
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleReset = () => {
        // Keep errorCount bumped so children remount via key change,
        // clearing any broken hook state from the previous render.
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoToDashboard = () => {
        if (this.props.navigate) {
            this.props.navigate('/dashboard');
            this.handleReset();
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex justify-center items-center min-h-[60vh] p-6">
                    <div className="max-w-lg w-full text-center">
                        <AlertCircle size={64} className="text-[var(--error)] mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-[var(--text-main)] mb-2">Something went wrong</h2>
                        <p className="text-[var(--text-secondary)] mb-6">
                            We&apos;re sorry, but something unexpected happened. The error has been logged and we&apos;ll look into it.
                        </p>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="text-left mb-6 bg-[var(--bg-hover)] rounded-xl p-4">
                                <summary className="cursor-pointer font-semibold text-[var(--error)] mb-2">
                                    Error Details (Development Only)
                                </summary>
                                <p className="text-sm text-[var(--text-main)] mt-2">
                                    <strong>Error:</strong> {this.state.error.toString()}
                                </p>
                                {this.state.errorInfo && (
                                    <div className="mt-3">
                                        <strong className="text-sm text-[var(--text-main)]">Component Stack:</strong>
                                        <pre className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </div>
                                )}
                            </details>
                        )}
                        <div className="flex gap-3 justify-center flex-wrap">
                            {this.props.navigate && (
                                <button
                                    type="button"
                                    onClick={this.handleGoToDashboard}
                                    className="px-6 py-3 bg-[var(--primary)] text-[var(--primary-text)] rounded-2xl font-semibold hover:opacity-90 transition"
                                >
                                    Go to Dashboard
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={this.handleReset}
                                className="px-6 py-3 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-2xl font-semibold hover:bg-[var(--bg-elevated)] transition"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // errorCount key forces a remount of the subtree on reset, so any
        // hook state that was holding the bad value gets cleared.
        return <React.Fragment key={this.state.errorCount}>{this.props.children}</React.Fragment>;
    }
}

export interface ErrorBoundaryProps {
    children?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}
export const ErrorBoundary = ({ children, onError }: ErrorBoundaryProps) => {
    const navigate = useNavigate();
    return (
        <ErrorBoundaryClass navigate={navigate} onError={onError}>
            {children}
        </ErrorBoundaryClass>
    );
};
